import React, { useCallback, useEffect, useRef, useState } from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";

import { NSApps } from "../types";
import UseCasesListItem from "./UseCasesListItem";

const LOAD_CATEGORIES_COUNT = 2;
const UseCasesList: React.FC<{ categories: NSApps.ICategoryItem[] }> = ({
  categories,
}) => {
  const loadMore = useRef<HTMLDivElement | null>(null);
  const [slicedCategories, setSlicedCategories] = useState(
    /**
     * If IntersectionObserver is not supported by browser then load all categories
     * */
    typeof window !== "undefined" &&
      typeof window.IntersectionObserver !== "undefined"
      ? categories.slice(0, LOAD_CATEGORIES_COUNT)
      : categories
  );
  const categoriesLength = categories.length;
  const slicedCategoriesLength = slicedCategories.length;

  const onLoadMore = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && categoriesLength > slicedCategoriesLength) {
        setSlicedCategories(
          (loadedCategories) =>
            [
              /**
               * Use Set so that we don't render the duplicate categories section
               * */
              ...new Set([
                ...loadedCategories,
                ...categories.slice(
                  slicedCategoriesLength,
                  /**
                   * Before slicing check if sliced categories array length should not be
                   * greater than categories array length
                   * */
                  slicedCategoriesLength + LOAD_CATEGORIES_COUNT >
                    categoriesLength
                    ? categoriesLength
                    : slicedCategoriesLength + LOAD_CATEGORIES_COUNT
                ),
              ]),
            ] as NSApps.ICategoryItem[]
        );
      }
    },
    [categories, categoriesLength, slicedCategoriesLength]
  );

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: "20px",
      threshold: 0,
    };
    const observer = new IntersectionObserver(onLoadMore, option);
    if (loadMore.current) observer.observe(loadMore.current);
  }, [onLoadMore]);

  return (
    <Container
      maxWidth="xl"
      sx={{
        maxWidth: "1365px !important",
      }}
    >
      {slicedCategories.map(({ id, ...props }) => (
        <UseCasesListItem key={id} id={id} {...props} />
      ))}
      {/* Conditionally render loadMore ref to avoid unnecessary 
      rendering once all categories are rendered */}
      {categoriesLength > slicedCategoriesLength && <div ref={loadMore} />}
    </Container>
  );
};

export default UseCasesList;
